import type { ImageProps } from 'next/image'
import Image from 'next/image'
import React, { useEffect, useRef, useState } from 'react'
import { twMerge } from 'tailwind-merge'

import { caption, h4 } from '@ui-kit/core/typography'
import { bgColor, borderColor, textColor } from '@ui-kit/styles/colors'
import { paddingTop } from '@ui-kit/styles/padding'
import type { Color, Space } from '@ui-kit/types'

import { productCard } from './styles'
import { CARD_ELEMENTS_VARIANTS, DEFAULT_BORDER_RADIUS, MAX_CARD_WIDTH } from './types'
import type { BorderRadius, CardElementVariant } from './types'

// Below this width the footer uses rows
const FOOTER_COLUMNS_MIN_WIDTH = 300

export const TagList = (props: { children: React.ReactNode }) => {
  const { tagList } = productCard({})
  return <ul className={tagList()}>{props.children}</ul>
}

export const Header = (props: { paddingTop?: Space; children: React.ReactNode }) => {
  const { header } = productCard({})
  return (
    <div
      className={header({
        className: paddingTop({ space: props.paddingTop ?? 'small' }),
      })}
    >
      {props.children}
    </div>
  )
}

export const Tag = (props: { text: string; variant: CardElementVariant }) => {
  const { tag } = productCard({})
  return (
    <li
      className={tag({
        className: twMerge(
          bgColor({ color: props.variant }),
          textColor({ color: CARD_ELEMENTS_VARIANTS[props.variant] })
        ),
      })}
    >
      {props.text}
    </li>
  )
}

export const Root = (props: {
  children: React.ReactNode
  borderRadius?: BorderRadius
  variant?: CardElementVariant
  borderColor?: Color
  maxWidth?: string | number
}) => {
  const { root } = productCard({})
  let rootBorderColor = props.borderColor
  if (!rootBorderColor) {
    rootBorderColor = props.variant ? CARD_ELEMENTS_VARIANTS[props.variant] : 'grey300'
  }
  return (
    <div
      className={root({
        className: twMerge(
          bgColor({
            color: props.variant ?? 'white',
          }),
          borderColor({
            color: rootBorderColor,
          })
        ),
      })}
      style={{
        borderRadius: props.borderRadius ?? DEFAULT_BORDER_RADIUS,
        maxWidth: props.maxWidth ?? MAX_CARD_WIDTH,
      }}
    >
      {props.children}
    </div>
  )
}

export const Cover = (props: { children: React.ReactNode; borderRadius?: BorderRadius }) => {
  const { cover } = productCard({})
  return (
    <div className={cover()} style={{ borderRadius: props.borderRadius ?? DEFAULT_BORDER_RADIUS }}>
      {props.children}
    </div>
  )
}

export const CoverImage = (props: ImageProps) => {
  const { coverImage } = productCard({})
  return <Image {...props} className={coverImage({ className: props.className })} />
}

export const HeaderTextContainer = (props: { children: React.ReactNode }) => {
  const { headerTextContainer } = productCard({})
  return <div className={headerTextContainer()}>{props.children}</div>
}

export const Title = (props: { children: React.ReactNode; className?: string }) => {
  return <div className={h4({ className: props.className })}>{props.children}</div>
}

export const Subtitle = (props: {
  children: React.ReactNode
  cardVariant?: CardElementVariant
}) => {
  return (
    <div
      className={caption({
        color: props.cardVariant ? CARD_ELEMENTS_VARIANTS[props.cardVariant] : 'grey500',
        emphasis: props.cardVariant ? 'medium' : 'default',
      })}
    >
      {props.children}
    </div>
  )
}

export const Content = (props: { children: React.ReactNode }) => {
  const { content } = productCard({})
  return <div className={content()}>{props.children}</div>
}

export const Footer = (props: { children: React.ReactNode }) => {
  const [width, setWidth] = useState<number | undefined>()
  const ref = useRef<HTMLDivElement>(null)
  const { footer } = productCard({})

  useEffect(() => {
    if (typeof window === 'undefined') {
      return
    }
    const updateWidth = () => {
      if (ref.current) {
        setWidth(ref.current.clientWidth)
      }
    }

    updateWidth()

    window.addEventListener('resize', updateWidth)

    return () => {
      window.removeEventListener('resize', updateWidth)
    }
  }, [])

  return (
    <div
      ref={ref}
      className={twMerge(footer(), width && width < FOOTER_COLUMNS_MIN_WIDTH && 'grid-flow-row')}
    >
      {props.children}
    </div>
  )
}
